import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ButtonProps } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import { useWeatherAlertList } from 'features/weatherAlerts/hooks/useWeatherAlertList';
import {
  GenericWeatherStation,
  WeatherAlert,
  WeatherStationType,
} from 'features/weatherAlerts/weatherAlerts.types';
import { useAuthState } from 'state/useAuthState';
import { MEMBERSHIP_PLANS } from 'features/membership/membership.constants';
import { getPaywallLink } from 'features/membership/membership.utils';

type UseCreateAlertButton = Pick<ButtonProps, 'disabled' | 'onClick'> & {
  component: typeof Link;
  to: string;
};

const getLinkPath = (props: {
  station?: GenericWeatherStation;
  canCreate: boolean;
}): string => {
  const { canCreate, station } = props;

  if (!canCreate || !station) {
    const source = station
      ? `create_weather_alert:${station.weatherStationType}`
      : 'create_weather_alert';
    const paywallLink = getPaywallLink({
      focusedPlan: MEMBERSHIP_PLANS.yearly,
      source,
    });
    return paywallLink.url;
  }

  if (station.weatherStationType === 'waterLevel') {
    return `/gauge/${station.id}/alert/create`;
  }

  if (station.weatherStationType === 'atmospheric') {
    return `/weather_station/${station.id}/alert/create`;
  }

  return `/aqi/${station.id}/alert/create`;
};

const canCreateAlertForStationType = (
  showMembershipFeatures: boolean,
  stationType: WeatherStationType,
  alerts: WeatherAlert[],
): boolean =>
  showMembershipFeatures ||
  !alerts.some((alert) => alert.station.weatherStationType === stationType);

export const useCreateAlertButton = (
  station?: GenericWeatherStation,
): UseCreateAlertButton => {
  const { t } = useTranslation();
  const { showMembershipFeatures } = useAuthState();
  const { alerts = [], isLoading, isError } = useWeatherAlertList();

  return {
    component: Link,
    to: getLinkPath({
      station,
      canCreate:
        station !== undefined &&
        canCreateAlertForStationType(
          showMembershipFeatures,
          station.weatherStationType,
          alerts,
        ),
    }),
    disabled: !station || isLoading,
    onClick: isError
      ? (e) => {
          e.preventDefault();
          enqueueSnackbar(t('common.errorMessage'), { variant: 'error' });
        }
      : undefined,
  };
};
